import type { FeatureCollection, Geometry } from "geojson";

import { LatLng } from "./geocode";

/**
 * Postcode DISTRICT (outward code, e.g. "PL1") helpers for the onboarding wizard.
 *
 * Polygons + index live in public/postcode-districts (see scripts/fetch-postcode-polygons.mjs). The index holds a
 * centroid and bounding box per district so we can decide which districts fall inside a radius without loading
 * every polygon; polygons are only fetched for the postcode AREAS ("PL") that were touched.
 */

export interface DistrictIndexEntry {
  /** Centroid [lat, lng] */
  c: LatLng;
  /** [minLng, minLat, maxLng, maxLat] */
  bbox: [number, number, number, number];
}

export interface DistrictIndex {
  source: string;
  generated: string;
  areas: Record<
    string,
    { bbox: [number, number, number, number]; districts: Record<string, DistrictIndexEntry> }
  >;
}

export interface DistrictProperties {
  name: string;
}

export type DistrictFeatureCollection = FeatureCollection<Geometry, DistrictProperties>;

export interface GeocodedPostcode {
  postcode: string;
  outcode: string;
  latLng: LatLng;
  town?: string;
}

export interface DistrictInRadius {
  district: string;
  area: string;
  centre: LatLng;
  distanceMiles: number;
}

const BASE = `${process.env.PUBLIC_URL ?? ""}/postcode-districts`;
const EARTH_RADIUS_MILES = 3958.7613;

let indexPromise: Promise<DistrictIndex> | undefined;
const polygonCache = new Map<string, Promise<DistrictFeatureCollection>>();

/** "PL10" -> "PL" (letters before the first digit). */
export function areaOf(district: string): string {
  return district.match(/^[A-Z]+/i)?.[0].toUpperCase() ?? "";
}

/**
 * The outward code to show or match for a district row.
 * Rows store either the full outcode ("SW1Y") or only the part after the area ("1Y").
 * Callers that prefix the area again must use this, or "SW" + "SW1Y" renders as "SWSW1Y".
 */
export function districtOutcode(area: string, district: string): string {
  const areaCode = (area ?? "").replace(/\s+/g, "").toUpperCase();
  const code = (district ?? "").replace(/\s+/g, "").toUpperCase();
  if (areaCode.length === 0) return code;
  if (code.startsWith(areaCode)) return code;
  return areaCode + code;
}

/** Normalise user input: "pl1 0lf" -> "PL1 0LF" ; also tolerates an outcode on its own ("pl1" -> "PL1"). */
export function normalisePostcode(input: string): string {
  const compact = input.replace(/\s+/g, "").toUpperCase();
  if (compact.length <= 4) return compact;
  return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
}

export function haversineMiles(a: LatLng, b: LatLng): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Single postcode lookup via postcodes.io. Returns undefined when the postcode is not recognised. */
export async function geocodePostcode(input: string): Promise<GeocodedPostcode | undefined> {
  const postcode = normalisePostcode(input);
  if (!postcode) return undefined;

  const res = await fetch(
    `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`
  );
  if (res.status === 404) return undefined;
  if (!res.ok) throw new Error(`Postcode lookup failed (${res.status})`);

  const body = await res.json();
  if (body.status !== 200 || !body.result) return undefined;

  return {
    postcode: body.result.postcode,
    outcode: body.result.outcode,
    latLng: [body.result.latitude, body.result.longitude],
    town: body.result.admin_district ?? body.result.parish ?? undefined,
  };
}

/** Best-effort town names for a set of outcodes (one postcodes.io call each, limited concurrency). */
export async function lookupDistrictTowns(
  districts: string[]
): Promise<Record<string, string | undefined>> {
  const result: Record<string, string | undefined> = {};
  const queue = [...districts];

  const worker = async () => {
    while (queue.length) {
      const district = queue.shift()!;
      try {
        const res = await fetch(
          `https://api.postcodes.io/outcodes/${encodeURIComponent(district)}`
        );
        const body = res.ok ? await res.json() : undefined;
        const towns: string[] | undefined = body?.result?.admin_district;
        result[district] = towns?.[0] ?? undefined;
      } catch {
        result[district] = undefined;
      }
    }
  };

  await Promise.all(Array.from({ length: 6 }, worker));
  return result;
}

/**
 * Fetch one of our static JSON assets. SPA hosting (Azure SWA, the CRA dev server) answers a missing file with a
 * 200 + index.html, which would otherwise surface as "Unexpected token '<' is not valid JSON"; turn that into a
 * clear "missing" error instead. Content-type can't be trusted (.geojson is served as octet-stream).
 */
async function fetchStaticJson<T>(url: string, what: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${what} is missing (${res.status})`);
  const text = await res.text();
  if (/^\s*</.test(text)) throw new Error(`${what} is missing (server returned a page instead of data)`);
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`${what} is corrupt`);
  }
}

export function loadDistrictIndex(): Promise<DistrictIndex> {
  if (!indexPromise) {
    indexPromise = fetchStaticJson<DistrictIndex>(`${BASE}/index.json`, "Postcode district index");
    indexPromise.catch(() => (indexPromise = undefined));
  }
  return indexPromise;
}

export function loadAreaPolygons(area: string): Promise<DistrictFeatureCollection> {
  const key = area.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{1,2}$/.test(key)) {
    return Promise.reject(new Error(`"${area}" is not a postcode area`));
  }
  if (!polygonCache.has(key)) {
    const p = fetchStaticJson<DistrictFeatureCollection>(`${BASE}/${key}.geojson`, `Polygons for postcode area ${key}`);
    p.catch(() => polygonCache.delete(key));
    polygonCache.set(key, p);
  }
  return polygonCache.get(key)!;
}

/**
 * Polygons for several areas at once. Areas that fail (no data for BT / GY / JE / IM, a malformed group postcode…)
 * are skipped with a console warning rather than failing the whole map; throws only if nothing could be loaded.
 */
export async function loadAreasPolygons(
  areas: string[]
): Promise<Record<string, DistrictFeatureCollection>> {
  const settled = await Promise.allSettled(
    areas.map(async (a) => [a.replace(/\s+/g, "").toUpperCase(), await loadAreaPolygons(a)] as const)
  );

  const out: Record<string, DistrictFeatureCollection> = {};
  const failures: string[] = [];
  settled.forEach((s, i) => {
    if (s.status === "fulfilled") out[s.value[0]] = s.value[1];
    else {
      failures.push(areas[i]);
      console.warn(`Skipping postcode area ${areas[i]}:`, s.reason?.message ?? s.reason);
    }
  });

  if (areas.length > 0 && Object.keys(out).length === 0) {
    throw new Error(`No polygon data for ${failures.join(", ")}`);
  }
  return out;
}

/**
 * Every district whose centroid lies within `radiusMiles` of `centre`. The district containing the yard
 * (`alwaysInclude`, normally the geocoded outcode) is included even if its centroid is outside the radius.
 * Uses area bounding boxes as a cheap pre-filter.
 */
export function districtsWithinRadius(
  index: DistrictIndex,
  centre: LatLng,
  radiusMiles: number,
  alwaysInclude?: string
): DistrictInRadius[] {
  const [lat, lng] = centre;
  // ~1 degree latitude = 69 miles; widen longitude by cos(lat)
  const dLat = radiusMiles / 69;
  const dLng = radiusMiles / (69 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));

  const results: DistrictInRadius[] = [];

  for (const [area, info] of Object.entries(index.areas)) {
    const [minLng, minLat, maxLng, maxLat] = info.bbox;
    const overlaps =
      minLng <= lng + dLng && maxLng >= lng - dLng && minLat <= lat + dLat && maxLat >= lat - dLat;
    if (!overlaps && alwaysInclude?.toUpperCase().startsWith(area) !== true) continue;

    for (const [district, entry] of Object.entries(info.districts)) {
      const distanceMiles = haversineMiles(centre, entry.c);
      if (distanceMiles <= radiusMiles || district === alwaysInclude?.toUpperCase()) {
        results.push({ district, area, centre: entry.c, distanceMiles });
      }
    }
  }

  return results.sort(compareDistricts);
}

/** All districts in the given areas (used to write de-selected districts as inactive rows). */
export function allDistrictsInAreas(index: DistrictIndex, areas: string[]): string[] {
  const out: string[] = [];
  for (const area of areas) {
    const info = index.areas[area.toUpperCase()];
    if (info) out.push(...Object.keys(info.districts));
  }
  return out.sort((a, b) => compareDistrictNames(a, b));
}

/** Natural sort: PL1, PL2, ..., PL10 rather than PL1, PL10, PL2. */
export function compareDistrictNames(a: string, b: string): number {
  const [, aArea = "", aNum = "0", aRest = ""] = a.match(/^([A-Z]+)(\d+)(.*)$/i) ?? [];
  const [, bArea = "", bNum = "0", bRest = ""] = b.match(/^([A-Z]+)(\d+)(.*)$/i) ?? [];
  if (aArea !== bArea) return aArea.localeCompare(bArea);
  if (+aNum !== +bNum) return +aNum - +bNum;
  return aRest.localeCompare(bRest);
}

function compareDistricts(a: DistrictInRadius, b: DistrictInRadius): number {
  return compareDistrictNames(a.district, b.district);
}
